import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Warning, EnvelopeSimple } from '@phosphor-icons/react';
import { PillButton } from '../components/PillKit';
import { authExtrasAPI, formatApiError } from '../services/api';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png';

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token') || '', [params]);
  const [status, setStatus] = useState('loading'); // loading | success | error | no-token
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) { setStatus('no-token'); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await authExtrasAPI.verifyEmail(token);
        if (!cancelled) { setMessage(data.message || 'Email verified.'); setStatus('success'); }
      } catch (err) {
        if (!cancelled) { setMessage(formatApiError(err)); setStatus('error'); }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const isError = status === 'error' || status === 'no-token';
  const iconColor = status === 'success' ? '#22C55E' : isError ? '#EF4444' : '#22C55E';

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="verify-email-page">
      <header className="px-5 pt-6 pb-2 flex items-center justify-center">
        <Link to="/"><img src={LOGO_URL} alt="Divispend" className="h-10 w-auto" /></Link>
      </header>

      <main className="flex-1 max-w-md w-full mx-auto px-6 pt-10 pb-12 text-center">
        <div
          className="w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-7 border-[3px]"
          style={{ borderColor: iconColor, backgroundColor: `${iconColor}1A` }}
        >
          {status === 'loading' && <EnvelopeSimple className="w-12 h-12 text-[#22C55E] animate-pulse" weight="duotone" />}
          {status === 'success' && <CheckCircle className="w-12 h-12 text-[#22C55E]" weight="fill" />}
          {isError && <Warning className="w-12 h-12 text-red-500" weight="fill" />}
        </div>

        {status === 'loading' && (
          <div data-testid="verify-loading">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Verifying…
            </h1>
            <p className="text-white/60 text-sm">Hold on one moment.</p>
          </div>
        )}

        {status === 'success' && (
          <div data-testid="verify-success">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              You&apos;re verified!
            </h1>
            <p className="text-[#22C55E] text-base sm:text-lg font-bold tracking-tight mb-6" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Welcome to Divispend
            </p>
            <p className="text-white/60 text-sm mb-8">{message}</p>
            <Link to="/dashboard">
              <PillButton variant="solid" type="button" data-testid="verify-continue-btn">
                Continue to Divispend
              </PillButton>
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div data-testid="verify-error">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Verification failed
            </h1>
            <p className="text-white/60 text-sm mb-8">{message}</p>
            <Link to="/login">
              <PillButton variant="solid" type="button" data-testid="verify-error-signin-btn">
                Back to sign in
              </PillButton>
            </Link>
          </div>
        )}

        {status === 'no-token' && (
          <div data-testid="verify-no-token">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Link looks broken
            </h1>
            <p className="text-white/60 text-sm mb-8">
              No verification token in the URL. Head to your dashboard and request a new email.
            </p>
            <Link to="/dashboard">
              <PillButton variant="solid" type="button" data-testid="verify-dashboard-btn">
                Back to dashboard
              </PillButton>
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
